/**
 * Directus API Client
 * Handles all API interactions with the Directus backend
 */

import { createDirectus, rest, authentication, readItems, readItem, createItem, updateItem, deleteItem, withToken, readMe, readUsers, readUser, updateUser, customEndpoint, logout } from '@directus/sdk';

// Configuration
const DIRECTUS_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DIRECTUS_URL) || 'http://localhost:8055';

// Enhanced logging for development
if (import.meta.env.DEV) {
  // Intercept all fetch requests
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const [url, options = {}] = args;
    
    console.group(`🌐 Fetch Request: ${url}`);
    console.log('Method:', options.method || 'GET');
    console.log('Headers:', options.headers);
    console.log('Body:', options.body);
    console.log('Credentials:', options.credentials);
    
    const startTime = performance.now();
    
    try {
      const response = await originalFetch(...args);
      const endTime = performance.now();
      
      console.log(`📡 Response (${Math.round(endTime - startTime)}ms):`, {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        url: response.url
      });
      
      // Clone response to read body without consuming it
      const responseClone = response.clone();
      try {
        const responseText = await responseClone.text();
        if (responseText) {
          console.log('Response body:', responseText.substring(0, 500) + (responseText.length > 500 ? '...' : ''));
        }
      } catch (e) {
        console.log('Could not read response body');
      }
      
      console.groupEnd();
      return response;
    } catch (error) {
      const endTime = performance.now();
      console.error(`❌ Fetch Error (${Math.round(endTime - startTime)}ms):`, error);
      console.groupEnd();
      throw error;
    }
  };
}

/**
 * Directus client instance
 */
export const directusClient = createDirectus(DIRECTUS_URL)
  .with(rest({ credentials: 'include' }))
  .with(authentication('cookie', { credentials: 'include' }));

/**
 * API service for interacting with Directus collections
 */
/**
 * Base URL for the Directus API
 */
export const baseURL = DIRECTUS_URL;

export const api = {
  /**
   * Search across all content types
   * @param {string} query - Search query (can be empty for browsing)
   * @param {Object} options - Search options (type, filters, limit, offset, additionalFilters)
   * @returns {Promise<Object>} Search results
   */
  async search(query, options = {}) {
    const { type, limit = 20, offset = 0, filters = {}, additionalFilters = [] } = options;
    
    // Build collection-specific search filters
    const buildSearchFilter = (collection) => {
      // If no query, return null (browse all)
      if (!query || query.trim() === '') {
        return null;
      }
      
      const searchFields = {
        papers: ['title', 'abstract'],
        people: ['name', 'biography'],
        institutions: ['name', 'description'],
        projects: ['title', 'focus'],
        datasets: ['title', 'description'],
        events: ['title', 'description']
      };
      
      const fields = searchFields[collection] || ['name', 'title'];
      return {
        _or: fields.map(field => ({ [field]: { _icontains: query } }))
      };
    };

    // Get collection-specific fields for relationships
    const getCollectionFields = (collection) => {
      const fieldMaps = {
        papers: [
          '*',
          'authors.people_id.*',
          'institutions.institutions_id.*',
          'subjects.subjects_id.*'
        ],
        people: [
          '*',
          'affiliations.institutions_id.*'
        ],
        institutions: ['*'],
        projects: [
          '*',
          'investigators.people_id.*',
          'funding_institution.institutions_id.*',
          'related_papers.papers_id.*',
          'related_datasets.datasets_id.*'
        ],
        datasets: [
          '*',
          'creators.people_id.*',
          'institutions.institutions_id.*',
          'papers.papers_id.*'
        ],
        events: [
          '*',
          'institutions.institutions_id.*'
        ]
      };
      
      return fieldMaps[collection] || ['*'];
    };
    
    // If type is specified, search only that collection
    if (type) {
      const searchFilter = buildSearchFilter(type);
      const filterConditions = [
        { status: { _eq: 'published' } },
        ...Object.entries(filters).map(([key, value]) => ({ [key]: value })),
        ...additionalFilters
      ];
      
      // Only add search filter if query exists
      if (searchFilter) {
        filterConditions.unshift(searchFilter);
      }
      
      const filter = { _and: filterConditions };
      const fields = getCollectionFields(type);
      
      return this.getItems(type, { filter, limit, offset, fields });
    }
    
    // Otherwise, search across all collections
    const collections = ['papers', 'people', 'institutions', 'projects', 'datasets', 'events'];
    const results = await Promise.all(
      collections.map(collection => {
        const searchFilter = buildSearchFilter(collection);
        const filterConditions = [
          { status: { _eq: 'published' } },
          ...Object.entries(filters).map(([key, value]) => ({ [key]: value })),
          ...additionalFilters
        ];
        
        // Only add search filter if query exists
        if (searchFilter) {
          filterConditions.push(searchFilter);
        }
        
        const filter = { _and: filterConditions };
        const fields = getCollectionFields(collection);
        
        return this.getItems(collection, { filter, limit: 5, fields }).catch(() => ({ data: [] }));
      })
    );
    
    return {
      papers: results[0]?.data || [],
      people: results[1]?.data || [],
      institutions: results[2]?.data || [],
      projects: results[3]?.data || [],
      datasets: results[4]?.data || [],
      events: results[5]?.data || []
    };
  },
  
  /**
   * Get items from a collection with enhanced logging
   * @param {string} collection - Collection name
   * @param {Object} options - Query options (filter, fields, limit, offset, sort)
   * @returns {Promise<Object>} Items data
   */
  async getItems(collection, options = {}) {
    console.group(`🔍 API Request: ${collection}`);
    console.log('Options:', JSON.stringify(options, null, 2));
    console.log('URL will be:', `${DIRECTUS_URL}/items/${collection}`);
    
    try {
      const startTime = performance.now();
      const data = await directusClient.request(readItems(collection, options));
      const endTime = performance.now();
      
      console.log(`✅ Success (${Math.round(endTime - startTime)}ms):`, {
        collection,
        itemCount: data?.length || 0,
        firstItem: data?.[0] ? Object.keys(data[0]) : 'No items'
      });
      console.groupEnd();
      
      return { data, error: null };
    } catch (error) {
      console.error(`❌ Error (${collection}):`, {
        message: error.message,
        errors: error.errors,
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          url: error.response.url
        } : null,
        stack: error.stack
      });
      
      // Try to get response body if available
      if (error.response && !error.response.bodyUsed) {
        try {
          const responseText = await error.response.text();
          console.error('Response body:', responseText);
        } catch (e) {
          console.error('Could not read response body:', e);
        }
      }
      
      console.groupEnd();
      return { data: null, error: error.message };
    }
  },
  
  /**
   * Get a single item by ID
   * @param {string} collection - Collection name
   * @param {string} id - Item ID
   * @param {Object} options - Query options (fields)
   * @returns {Promise<Object>} Item data
   */
  async getItem(collection, id, options = {}) {
    try {
      const data = await directusClient.request(readItem(collection, id, options));
      return { data, error: null };
    } catch (error) {
      console.error(`Error fetching ${collection} item:`, error);
      return { data: null, error: error.message };
    }
  },
  
  /**
   * Create a new item
   * @param {string} collection - Collection name
   * @param {Object} data - Item data
   * @returns {Promise<Object>} Created item
   */
  async createItem(collection, data) {
    try {
      const result = await directusClient.request(createItem(collection, data));
      return { data: result, error: null };
    } catch (error) {
      console.error(`Error creating ${collection} item:`, error);
      return { data: null, error: error.message };
    }
  },
  
  /**
   * Update an existing item
   * @param {string} collection - Collection name
   * @param {string} id - Item ID
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} Updated item
   */
  async updateItem(collection, id, data) {
    try {
      const result = await directusClient.request(updateItem(collection, id, data));
      return { data: result, error: null };
    } catch (error) {
      console.error(`Error updating ${collection} item:`, error);
      return { data: null, error: error.message };
    }
  },
  
  /**
   * Delete an item
   * @param {string} collection - Collection name
   * @param {string} id - Item ID
   * @returns {Promise<Object>} Delete result
   */
  async deleteItem(collection, id) {
    try {
      await directusClient.request(deleteItem(collection, id));
      return { success: true, error: null };
    } catch (error) {
      console.error(`Error deleting ${collection} item:`, error);
      return { success: false, error: error.message };
    }
  },
  
  /**
   * Get papers with related data
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Papers with authors and institutions
   */
  async getPapers(options = {}) {
    return this.getItems('papers', {
      ...options,
      fields: [
        '*',
        'authors.people_id.*',
        'institutions.institutions_id.*',
        'subjects.subjects_id.*'
      ]
    });
  },
  
  /**
   * Get people with related data
   * @param {Object} options - Query options
   * @returns {Promise<Object>} People with affiliations
   */
  async getPeople(options = {}) {
    return this.getItems('people', {
      ...options,
      fields: [
        '*',
        'affiliations.institutions_id.*'
      ]
    });
  },
  
  /**
   * Get institutions with related data
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Institutions
   */
  async getInstitutions(options = {}) {
    return this.getItems('institutions', options);
  },
  
  /**
   * Get projects with related data
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Projects with investigators and outputs
   */
  async getProjects(options = {}) {
    return this.getItems('projects', {
      ...options,
      fields: [
        '*',
        'investigators.people_id.*',
        'funding_institution.institutions_id.*',
        'related_papers.papers_id.*',
        'related_datasets.datasets_id.*'
      ]
    });
  },

  /**
   * Get datasets with related data
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Datasets with creators and related content
   */
  async getDatasets(options = {}) {
    return this.getItems('datasets', {
      ...options,
      fields: [
        '*',
        'creators.people_id.*',
        'institutions.institutions_id.*',
        'papers.papers_id.*'
      ]
    });
  },
  
  /**
   * Get subjects (controlled vocabulary)
   * @returns {Promise<Object>} Subjects list
   */
  async getSubjects() {
    return this.getItems('subjects', {
      sort: ['name']
    });
  },
  
  /**
   * Get tags
   * @returns {Promise<Object>} Tags list
   */
  async getTags() {
    return this.getItems('tags', {
      sort: ['-usage_count', 'name']
    });
  },

  /**
   * Fetch DOI metadata from Crossref
   * @param {string} doi - DOI to fetch
   * @returns {Promise<Object>} Metadata from Crossref
   */
  async fetchDOIMetadata(doi, type = 'paper') {
    console.log('🔍 API.fetchDOIMetadata called with DOI:', doi, 'type:', type);

    try {
      const url = `${DIRECTUS_URL}/doi-fetch?doi=${encodeURIComponent(doi)}&type=${encodeURIComponent(type)}`;
      console.log('📡 Fetching from URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      console.log('📦 Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Error response:', errorData);
        const errorMessage = errorData.errors?.[0]?.message || 'Failed to fetch DOI metadata';
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('✅ Success response:', data);
      return { data: data.data, error: null };
    } catch (error) {
      console.error('❌ Error fetching DOI metadata:', error);
      return { data: null, error: error.message };
    }
  },

  /**
   * Get users with filtering and pagination
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Users list
   */
  async getUsers(options = {}) {
    try {
      const data = await directusClient.request(readUsers(options));
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { data: null, error: error.message };
    }
  },

  /**
   * Get user roles
   * @returns {Promise<Object>} Roles list
   */
  async getRoles() {
    try {
      // Use custom endpoint for roles since there's no readRoles function
      const data = await directusClient.request(
        customEndpoint({
          path: '/roles',
          method: 'GET',
        })
      );
      return { data: data.data, error: null };
    } catch (error) {
      console.error('Error fetching roles:', error);
      return { data: null, error: error.message };
    }
  },

  /**
   * Update user role
   * @param {string} userId - User ID
   * @param {string} roleId - Role ID
   * @returns {Promise<Object>} Update result
   */
  async updateUserRole(userId, roleId) {
    try {
      const data = await directusClient.request(updateUser(userId, { role: roleId }));
      return { data, error: null };
    } catch (error) {
      console.error('Error updating user role:', error);
      return { data: null, error: error.message };
    }
  },

  /**
   * Bulk update users
   * @param {Array} userIds - Array of user IDs
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Bulk update result
   */
  async bulkUpdateUsers(userIds, updateData) {
    const results = [];
    for (const userId of userIds) {
      try {
        const data = await directusClient.request(updateUser(userId, updateData));
        results.push({ userId, success: true, error: null });
      } catch (error) {
        results.push({ userId, success: false, error: error.message });
      }
    }
    return { results, error: null };
  }
};

// Promise cache for the current user — stores the in-flight/resolved promise so
// concurrent calls share one network request instead of each firing their own.
let _currentUserPromise;

/**
 * Authentication service
 */
export const auth = {
  /**
   * Request storage access for cross-site cookies
   * Required for Chrome 115+ when using SameSite=None cookies
   * @returns {Promise<boolean>} Whether storage access was granted
   */
  async requestStorageAccess() {
    // Check if we're in a cross-site context
    const directusUrl = new URL(DIRECTUS_URL);
    const currentOrigin = window.location.origin;
    
    if (directusUrl.origin === currentOrigin) {
      // Same-site, no storage access needed
      return true;
    }
    
    try {
      // Check if Storage Access API is available
      if (!document.hasStorageAccess || !document.requestStorageAccess) {
        console.warn('Storage Access API not available');
        return false;
      }
      
      // Check if we already have access
      const hasAccess = await document.hasStorageAccess();
      if (hasAccess) {
        console.log('✅ Storage access already granted');
        return true;
      }
      
      // Request storage access
      console.log('🔐 Requesting storage access for cross-site cookies...');
      await document.requestStorageAccess();
      console.log('✅ Storage access granted');
      return true;
      
    } catch (error) {
      console.error('❌ Storage access denied:', error);
      return false;
    }
  },

  /**
   * Login user with cookie-based authentication
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {Object} options - Login options (mode, etc.)
   * @returns {Promise<Object>} Login result
   */
  async login(email, password, options = {}) {
    try {
      // Request storage access first for cross-site cookies
      await this.requestStorageAccess();
      
      console.log('Attempting login with fallback to direct API...');
      console.log('Login parameters:', {
        email: email,
        password: password ? '[REDACTED]' : password,
        emailType: typeof email,
        passwordType: typeof password
      });
      
      // First try the working direct API approach from debug-auth.html
      console.log('Trying direct API approach first...');
      const response = await fetch(`${DIRECTUS_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: email,
          password: password,
          mode: 'session'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Direct API login failed:', errorData);
        throw new Error(errorData.errors?.[0]?.message || 'Login failed');
      }
      
      console.log('Direct API login successful, fetching user data...');
      
      // Get current user with role information using SDK (session should be established)
      const user = await directusClient.request(readMe({
        fields: ['*', 'role.*']
      }));
      
      console.log('User fetched successfully:', user);
      return { user, error: null };
      
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific error cases
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (error.message) {
        if (error.message.includes('Invalid user credentials')) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (error.message.includes('User suspended')) {
          errorMessage = 'Your account has been suspended. Please contact an administrator.';
        } else {
          errorMessage = error.message;
        }
      }
      
      return { user: null, error: errorMessage };
    }
  },
  
  /**
   * Logout user
   * @returns {Promise<Object>} Logout result
   */
  async logout() {
    try {
      console.log('Attempting logout with direct API...');
      
      // Use direct API approach like login does
      const response = await fetch(`${DIRECTUS_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          mode: 'session'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Direct API logout failed:', errorData);
        throw new Error(errorData.errors?.[0]?.message || 'Logout failed');
      }
      
      _currentUserPromise = undefined;
      console.log('Logout successful');
      return { success: true, error: null };
    } catch (error) {
      console.group('❌ Logout Error Details');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.groupEnd();
      
      return { success: false, error: error.message };
    }
  },
  
  /**
   * Get current user (promise-cached per page load — concurrent callers share one request)
   * @returns {Promise<Object>} Current user data
   */
  getCurrentUser() {
    if (!_currentUserPromise) {
      _currentUserPromise = (async () => {
        try {
          await this.requestStorageAccess();
          return await directusClient.request(readMe({ fields: ['*', 'role.*'] }));
        } catch {
          return null;
        }
      })();
    }
    return _currentUserPromise;
  },
  
  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Registration result
   */
  async register(userData) {
    try {
      const user = await directusClient.request(createItem('directus_users', userData));
      return { user, error: null };
    } catch (error) {
      console.error('Registration error:', error);
      return { user: null, error: error.message };
    }
  },
  
  /**
   * Request password reset
   * @param {string} email - User email
   * @param {string} resetUrl - Custom reset URL (optional)
   * @returns {Promise<Object>} Reset request result
   */
  async requestPasswordReset(email, resetUrl = null) {
    try {
      const payload = { email };
      if (resetUrl) {
        payload.reset_url = resetUrl;
      }
      
      const response = await fetch(`${DIRECTUS_URL}/auth/password/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        return { success: true, error: null };
      } else {
        throw new Error('Password reset request failed');
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      return { success: false, error: error.message };
    }
  },
  
  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} password - New password
   * @returns {Promise<Object>} Reset result
   */
  async resetPassword(token, password) {
    try {
      const response = await fetch(`${DIRECTUS_URL}/auth/password/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          password: password
        })
      });
      
      if (response.ok) {
        return { success: true, error: null };
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Password reset failed');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: error.message };
    }
  }
};

/**
 * Get current user from client
 */
directusClient.getCurrentUser = auth.getCurrentUser;
